"use client";

import Image from "next/image";

import React, { useEffect, useRef, useState } from "react";
import Marquee from "react-fast-marquee";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Real traveler story graphics (screenshots + user captions), provided by the
// requester. Alt text summarizes each caption for screen readers, since the
// text is baked into the image.
const STORIES = [
  {
    src: "/assets/landing-page/stories/story-01.jpg",
    alt: "Traveler post: I used to manage our family's travel loyalty points in a spreadsheet for years, but Gondola has been life changing.",
  },
  {
    src: "/assets/landing-page/stories/story-02.jpg",
    alt: "Traveler post: the historical pricing scale is one of the most underrated features on Gondola.",
  },
  {
    src: "/assets/landing-page/stories/story-03.jpg",
    alt: "Traveler post: I've been using Gondola to book my travel lately and it's completely changed the game for me — it's like Google Flights for hotels.",
  },
  {
    src: "/assets/landing-page/stories/story-04.jpg",
    alt: "Traveler post: our rate dropped three separate times after we booked this car rental and Gondola caught it, saving us $117.",
  },
  {
    src: "/assets/landing-page/stories/story-05.jpg",
    alt: "Traveler post: Gondola found $435 in United credits expiring soon, so of course I booked a flight.",
  },
  {
    src: "/assets/landing-page/stories/story-06.jpg",
    alt: "Traveler post: we're going to Bali! We booked this on Gondola and got 7% back on top of our Hyatt points.",
  },
  {
    src: "/assets/landing-page/stories/story-07.jpg",
    alt: "Traveler post: I'm finally getting around to booking my hotel to Austin next month and Gondola makes it so easy.",
  },
  {
    src: "/assets/landing-page/stories/story-08.jpg",
    alt: "Traveler post: I earned 23% back on this hotel booking thanks to Gondola.",
  },
  {
    src: "/assets/landing-page/stories/story-09.jpg",
    alt: "Traveler post: I've been booking all of my work travel through Gondola and it gives me 7% Gondola Cash back on top of my loyalty points.",
  },
  {
    src: "/assets/landing-page/stories/story-10.jpg",
    alt: "Traveler post: my favorite kind of notification, thanks Gondola.",
  },
  {
    src: "/assets/landing-page/stories/story-11.jpg",
    alt: "Traveler post: I just checked my account and found a ridiculous amount of United flight credits because Gondola keeps automatically catching price drops.",
  },
  {
    src: "/assets/landing-page/stories/story-12.jpg",
    alt: "Traveler post: I think it's time for a vacation — shoutout Gondola for finding points I didn't even know I had.",
  },
];

// Native scroll containers have no concept of "infinite" — the only way to
// get a seamless loop in either direction (drag or arrows) is to render
// extra copies of the content and silently correct `scrollLeft` by exactly
// one copy-width the instant the viewport drifts into a copy that isn't the
// "real" one. Three copies (leading clone, real, trailing clone) is the
// standard shape: it gives a full copy's worth of scroll room on either
// side of the real one before a correction is ever needed. `REAL_COPY_INDEX`
// must stay the middle index — update both together if `COPIES` ever changes.
const COPIES = 3;
const REAL_COPY_INDEX = 1;

function StoryCard({ story }: { story: (typeof STORIES)[number] }) {
  return (
    <div className="w-[240px] shrink-0 overflow-hidden rounded-2xl border border-pure-white/15 sm:w-[280px]">
      <Image
        src={story.src}
        alt={story.alt}
        className="aspect-[1290/2293] h-auto w-full object-cover"
        width={1290}
        height={2293}
        sizes="(min-width: 500px) 280px, 240px"
        quality={85}
        loading="lazy"
      />
    </div>
  );
}

function measureStep(scroller: HTMLElement): number {
  // ".snap-start" must match the class applied to each card below.
  const card = scroller.querySelector<HTMLElement>(".snap-start");
  if (!card) return 0;
  const gap = parseFloat(getComputedStyle(scroller).columnGap || "0");
  return card.offsetWidth + gap;
}

const StoryCarousel: React.FC = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
  }, []);

  // Center on the real (middle) copy on mount, then correct `scrollLeft`
  // back into that copy's range the instant a drag or momentum scroll
  // carries it into a clone on either side. Because every copy is
  // pixel-identical, the correction is invisible — the row just keeps
  // scrolling.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    // `step`/`setWidth` start unmeasured (0) and only get a real value once
    // the row is actually laid out. This row is entirely hidden below `xl`
    // via the `xl:hidden` class on its wrapper, so it can mount with no
    // rendered box at all (e.g. the page loads at a desktop width) — measuring
    // then would read `offsetWidth: 0` on every card. `scroller.offsetWidth`
    // is the guard: it's 0 whenever an ancestor is `display: none`, and only
    // becomes positive once the row is genuinely visible.
    let step = 0;
    let setWidth = 0;

    // Re-measures card geometry and keeps `scrollLeft` aligned with it. Runs
    // on mount and on every `resize` — card width changes at the `sm`
    // breakpoint (240px -> 280px), and the row's own visibility toggles at
    // `xl`. Without re-anchoring `scrollLeft` here, either transition can
    // leave it at a pixel value that doesn't correspond to any card under the
    // new geometry: crossing `sm` mid-scroll desyncs it from the snap points
    // (surfacing as a visible native scroll-snap correction on the next
    // interaction), and revealing the row for the first time after it mounted
    // hidden leaves it uncentered in a clone copy instead of the real one.
    const syncToGeometry = () => {
      if (scroller.offsetWidth <= 0) return; // not laid out yet (e.g. hidden by `xl:hidden`)
      const newStep = measureStep(scroller);
      if (newStep <= 0 || newStep === step) return;
      // Bailing out above when the step hasn't actually changed matters:
      // `resize` fires for plenty of reasons that don't touch this row's
      // horizontal geometry (mobile address-bar collapse, a keyboard
      // opening), and re-anchoring unconditionally would force `scrollLeft`
      // to the nearest card boundary on every one of them — including while
      // the user's finger is mid-drag, fighting native touch tracking for
      // no reason.
      const newSetWidth = STORIES.length * newStep;
      // `scrollTo`/`scrollBy` default to "auto" behavior, which on this
      // element defers to its own `scroll-smooth` CSS class — so a plain
      // `scrollLeft = x` assignment (or an un-suffixed scrollBy/scrollTo call)
      // animates instead of jumping, undoing the whole point of these
      // corrections being invisible. `behavior: "instant"` is required on
      // every call here to actually get a jump.
      scroller.scrollTo({
        left:
          step > 0
            ? // Already measured once — keep the same card index rather
              // than re-centering, so a geometry change mid-scroll doesn't
              // visibly reset the row's position.
              Math.round(scroller.scrollLeft / step) * newStep
            : newSetWidth * REAL_COPY_INDEX,
        behavior: "instant",
      });
      step = newStep;
      setWidth = newSetWidth;
    };

    syncToGeometry();

    const handleScroll = () => {
      if (setWidth <= 0 || step <= 0) return;
      // Mount centers `scrollLeft` at exactly `setWidth` — the very first
      // pixel of the real copy, i.e. right at the correction threshold
      // below. With no margin, scrolling left even one pixel from a fresh
      // mount crosses the threshold on the first frame of a swipe or an
      // arrow's smooth scrollBy, so the instant correction fires mid-glide
      // and aborts it with a visible jump. Scrolling right doesn't have
      // this problem — there's a full copy-width of room before the other
      // threshold. A few cards of slack on both sides means ordinary
      // single-card navigation never reaches either threshold; only a much
      // longer flick or repeated stepping does.
      const margin = step * 2;
      if (scroller.scrollLeft < setWidth - margin) {
        scroller.scrollTo({
          left: scroller.scrollLeft + setWidth,
          behavior: "instant",
        });
      } else if (
        scroller.scrollLeft >=
        setWidth * (REAL_COPY_INDEX + 1) + margin
      ) {
        scroller.scrollTo({
          left: scroller.scrollLeft - setWidth,
          behavior: "instant",
        });
      }
    };

    scroller.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", syncToGeometry);
    return () => {
      scroller.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", syncToGeometry);
    };
  }, []);

  // Arrows step one card at a time. They don't need any wraparound logic of
  // their own — a plain scrollBy always has somewhere to go, since the
  // effect above keeps the scroller re-centered on an endless supply of
  // (visually identical) copies.
  const scrollByCard = (direction: "left" | "right") => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const step = measureStep(scroller);
    const behavior = prefersReducedMotion ? "instant" : "smooth";
    scroller.scrollBy({
      left: direction === "right" ? step : -step,
      behavior,
    });
  };

  // No solid circle backing at rest — just the chevron, with a drop-shadow
  // for legibility against whatever part of the card image sits behind it.
  // Hover gets only a faint scrim, not the bold circle this used to have.
  const arrowButtonClassName =
    "absolute top-1/2 z-10 -translate-y-1/2 text-pure-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] hover:bg-pure-black/15 hover:text-pure-white";

  return (
    <div className="w-full overflow-hidden">
      {/* Desktop/tablet (`xl`+): continuous auto-scrolling marquee. The
          press-logo strip above (BannerSlider.tsx) is the mirror image of
          this breakpoint split — a static row at `xl`+, its own marquee
          below `xl` — so the two rows deliberately diverge at every
          breakpoint, not just below `xl`. Below `xl`, this row switches to a
          user-swiped, infinitely-looping scroller instead of a second
          marquee: having two unrelated rows glide at the same fixed marquee
          speed read as an odd, slightly broken coincidence rather than two
          intentional behaviors. */}
      <div className="hidden xl:block">
        <Marquee
          className="w-full"
          direction="right"
          play={!prefersReducedMotion}
          gradient={false}
        >
          {STORIES.map((story) => (
            <div key={story.src} className="mx-2">
              <StoryCard story={story} />
            </div>
          ))}
        </Marquee>
      </div>

      {/* Mobile/tablet: native horizontal swipe (or the arrow buttons),
          snapping one card at a time, looping forever in either direction —
          the row never "runs out." No autoplay — this row is meant to read
          as browsable content the visitor drags (or taps) through, not
          passing scenery.
          `overscroll-behavior-x:contain` stops a flick that (rarely, given
          the loop above) reaches a true edge from chaining into the page's
          own vertical scroll on iOS Safari, which nested horizontal
          scrollers do by default.
          The row renders 3 copies of the 12 stories (see COPIES above) to
          make the loop possible; only the middle, "real" copy is exposed to
          screen readers (`aria-hidden` on the other two) so the same 12
          captions aren't announced three times. */}
      <div className="relative xl:hidden">
        <div
          ref={scrollerRef}
          className="flex w-full snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-5 pb-1 [-webkit-overflow-scrolling:touch] [overscroll-behavior-x:contain] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="group"
          aria-label="Traveler stories"
        >
          {Array.from({ length: COPIES }, (_, copy) =>
            STORIES.map((story) => (
              <div
                key={`${story.src}-${copy}`}
                className="snap-start"
                aria-hidden={copy !== REAL_COPY_INDEX}
              >
                <StoryCard story={story} />
              </div>
            ))
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => scrollByCard("left")}
          aria-label="Previous story"
          className={cn(arrowButtonClassName, "left-2")}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => scrollByCard("right")}
          aria-label="Next story"
          className={cn(arrowButtonClassName, "right-2")}
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default StoryCarousel;
